import { useState } from 'react';
import { Button, StatusDot, Toggle } from '@/components';
import type { TestConnectionResponse } from '@/lib/api';
import { AGENT_SCOPES, type AgentScope, type WorkspaceSettings } from '@shared/types';
import './ConnectionScreen.scss';

export interface ConnectionScreenProps {
  settings: WorkspaceSettings;
  apiMode: 'mock' | 'live';
  canEdit: boolean;
  busy: boolean;
  testResult: TestConnectionResponse | null;
  onConnect: (apiKey: string) => void;
  onDisconnect: () => void;
  onEnvironmentChange: (env: 'Production' | 'Sandbox') => void;
  onScopeToggle: (scope: AgentScope, enabled: boolean) => void;
  onTest: () => void;
}

const SCOPE_META: Record<AgentScope, { label: string; code: string }> = {
  stops: { label: 'Stops & deliveries', code: 'stops.read' },
  routes: { label: 'Routes & optimisation', code: 'routes.read' },
  drivers: { label: 'Drivers & fleet', code: 'drivers.read' },
  pod: { label: 'Proof of delivery', code: 'pod.read' },
  invoices: { label: 'Invoices & billing', code: 'invoices.read' },
};

export const ConnectionScreen: React.FC<ConnectionScreenProps> = ({
  settings,
  apiMode,
  canEdit,
  busy,
  testResult,
  onConnect,
  onDisconnect,
  onEnvironmentChange,
  onScopeToggle,
  onTest,
}) => {
  const [keyInput, setKeyInput] = useState('');
  const connected = settings.l2uConnected;

  return (
    <div className="screen screen--narrow">
      <div className="screen__eyebrow">Account API</div>
      <h1 className="screen__title">Locate2u connection</h1>
      <p className="screen__lede">
        Connect your Locate2u account so agents can query what&apos;s happening across stops, routes
        and drivers.
      </p>

      {apiMode === 'mock' && (
        <div className="connection__mode-note">
          This deployment is running against the sample account. Agents receive representative data,
          not your live operation. Switch <code>L2U_API_MODE</code> to <code>live</code> once the
          Locate2u API adapter is configured.
        </div>
      )}

      <div className="connection__hero">
        <StatusDot tone={connected ? 'live' : 'idle'} size={10} pulse={connected} />
        <div className="connection__hero-text">
          <div className="connection__hero-title">
            {connected ? 'Connected to Locate2u' : 'Not connected'}
          </div>
          <div className="connection__hero-meta">
            {connected
              ? `${settings.l2uEnvironment} · key ending ${settings.l2uKeyLastFour ?? '----'} · ${apiMode} adapter`
              : 'Add an API key to let agents read your account'}
          </div>
        </div>
        {connected && canEdit && (
          <Button variant="secondary" size="sm" onClick={onDisconnect}>
            Disconnect
          </Button>
        )}
      </div>

      {!connected && (
        <div className="connection__card">
          <div className="field-label">API key</div>
          <div className="connection__key-row">
            <input
              className="text-input text-input--mono"
              value={keyInput}
              placeholder="l2u_live_••••••••••••"
              disabled={!canEdit}
              onChange={(e) => setKeyInput(e.target.value)}
            />
            <Button loading={busy} disabled={!canEdit} onClick={() => onConnect(keyInput)}>
              Connect
            </Button>
          </div>
          <p className="connection__hint">
            Find your key in Locate2u → Settings → API integration. Only the last four characters
            are stored.
          </p>
        </div>
      )}

      {connected && (
        <>
          <div className="connection__grid">
            <div className="connection__card">
              <div className="field-label">Environment</div>
              <select
                className="select-input"
                value={settings.l2uEnvironment}
                disabled={!canEdit}
                onChange={(e) => onEnvironmentChange(e.target.value as 'Production' | 'Sandbox')}
              >
                <option value="Production">Production</option>
                <option value="Sandbox">Sandbox</option>
              </select>
            </div>
            <div className="connection__card">
              <div className="field-label">Key</div>
              <div className="connection__key-display">
                l2u_live_••••••••{settings.l2uKeyLastFour ?? '----'}
              </div>
              <div className="connection__key-note">read-only</div>
            </div>
          </div>

          <div className="connection__card connection__card--spaced">
            <div className="field-label">Data scopes</div>
            <p className="connection__hint connection__hint--tight">
              Agents can only query scopes enabled here.
            </p>
            <div className="connection__scopes">
              {AGENT_SCOPES.map((scope) => (
                <div key={scope} className="connection__scope">
                  <Toggle
                    checked={settings.l2uScopes[scope] ?? false}
                    disabled={!canEdit}
                    onChange={(v) => onScopeToggle(scope, v)}
                    label={SCOPE_META[scope].label}
                  />
                  <span className="connection__scope-code">{SCOPE_META[scope].code}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="connection__card connection__card--spaced">
            <div className="connection__test-head">
              <div>
                <div className="field-label">Test query</div>
                <div className="connection__hint connection__hint--tight">
                  GET /v2/stops?date=today
                </div>
              </div>
              <Button variant="secondary" size="sm" loading={busy} onClick={onTest}>
                Run test
              </Button>
            </div>

            {testResult && (
              <>
                <div
                  className={`connection__test-result${testResult.ok ? '' : ' connection__test-result--error'}`}
                >
                  {testResult.message}
                </div>
                {testResult.stops.length > 0 && (
                  <div className="preview-table">
                    <div className="preview-table__head">
                      <span>Stop</span>
                      <span>Customer</span>
                      <span>Status</span>
                      <span>ETA</span>
                    </div>
                    {testResult.stops.map((stop) => (
                      <div key={stop.id} className="preview-table__row">
                        <span className="preview-table__mono">{stop.id}</span>
                        <span className="preview-table__customer">{stop.customer}</span>
                        <span className="preview-table__status">
                          <StatusDot
                            tone={
                              stop.status === 'delivered'
                                ? 'live'
                                : stop.status === 'failed'
                                  ? 'danger'
                                  : 'warning'
                            }
                            size={6}
                          />
                          {stop.status}
                        </span>
                        <span className="preview-table__mono">{stop.eta}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ConnectionScreen;
